import { Typography } from "components/common";
import FilterHeader from "../FilterHeader";
import CallCenterCallItemCard from "../CallCenterCallItemCard";
import { useSelector, shallowEqual } from "react-redux";
import { RootState } from "redux/store";
import { CallCenterAgent } from "redux/slices/agents.slice";
import { Utils } from "utils";
import { AgentOffering } from "redux/slices/callcenter.slice";
import { useCallback, useMemo, useReducer, useState } from "react";
import { Button } from "antd";
import AppliedFilters from "../Filters/AppliedFilters";

type FilterState = {
  selectedQueue: string | null;
  selectedNumber: string | null;
  selectedDuration: string | null;
  selectedCallType: string | null;
  sortQueue: string | null;
  sortDuration: string | null;
  sortTime: string | null;
  sortAgent: string | null;
}

type FilterAction =
  | { type: "SET_FIELD"; field: keyof FilterState; value: string | string[] | null }
  | { type: "CLEAR_ALL" }
  | { type: "SET_BULK"; payload: Partial<FilterState> }

const QueuedCallsList: React.FC<any> = () => {
  const queueCallsObject = useSelector((state: RootState) => state.callCenter.queueCalls, shallowEqual);
  const queueCalls = useMemo(() => Object.values(queueCallsObject), [queueCallsObject]);
  const agents = useSelector((state: RootState) => state.agents.items, shallowEqual);
  const agentsOfferingObject = useSelector((state: RootState) => state.callCenter.agentsOffering, shallowEqual);
  const agentsOffering = useMemo(() => Object.values(agentsOfferingObject), [agentsOfferingObject]);
  const appsQueues = useSelector((state: RootState) => state.apps.data.queue, shallowEqual);
  const selectedDepartmentIds = useSelector((state: RootState) => state.callCenter.selectedDepartmentIds, shallowEqual);

  const filterOptionsData = useMemo(() => {
    const numbersSet = new Set<string>();

    queueCalls.forEach((call: any) => {
      const callerIDNumber = call.memberCIDNumber || call.callerIDNumber || '';
      if (callerIDNumber) {
        numbersSet.add(Utils.formatPhoneNumber(callerIDNumber));
      }
    });

    return {
      numbers: Array.from(numbersSet),
      queues: appsQueues.map((q: any) => q.name || q.extension),
    };
  }, [queueCalls, appsQueues]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const initialFilterState: FilterState = {
    selectedQueue: null,
    selectedNumber: null,
    selectedDuration: null,
    selectedCallType: null,
    sortQueue: null,
    sortDuration: null,
    sortTime: null,
    sortAgent: null,
  }

  function filterReducer(state: FilterState, action: FilterAction): FilterState {
    switch (action.type) {
      case "SET_FIELD":
        return { ...state, [action.field]: action.value }
      case "CLEAR_ALL":
        return { ...initialFilterState }
      case "SET_BULK":
        return { ...state, ...action.payload }
      default:
        return state
    }
  }

  const [filters, dispatchFilters] = useReducer(filterReducer, initialFilterState)
  const handleApplyFilters = useCallback((bulkFilters: Partial<FilterState>) => {
    dispatchFilters({ type: "SET_BULK", payload: bulkFilters })
  }, [])

  const handleClearFilters = useCallback(() => {
    dispatchFilters({ type: "CLEAR_ALL" })
  }, [])

  const handleRemoveFilter = useCallback((key: string) => {
    dispatchFilters({ type: "SET_FIELD", field: key as any, value: null })
  }, [])


  const agentNameMap: Record<string, string> = {};
  agents.forEach((agent: any) => {
    agentNameMap[agent.id] = agent.extension;
  });
  const offeringBySession: Record<string, AgentOffering[]> = {};
  agentsOffering.forEach((offering: any) => {
    if (!offeringBySession[offering.sessionUUID]) offeringBySession[offering.sessionUUID] = [];
    offeringBySession[offering.sessionUUID].push(offering);
  });

  const processActiveCalls = useMemo(() => {
    let processedCalls = [...queueCalls];

    // Filter by selected departments
    if (selectedDepartmentIds.length > 0) {
      const selectedDepartmentExtensions = appsQueues
        .filter((q: any) => selectedDepartmentIds.includes(q.id))
        .map((q: any) => q.extension);
      
      processedCalls = processedCalls.filter((call: any) => {
        let queueExtension = "";
        if (call.queueName) {
          queueExtension = call.queueName.split("@")[0];
        } else if (call.queue) {
          queueExtension = call.queue.split("@")[0];
        }
        return selectedDepartmentExtensions.includes(queueExtension);
      });
    }

    // Filter by Queue
    if (filters.selectedQueue && Array.isArray(filters.selectedQueue) && filters.selectedQueue.length > 0) {
      processedCalls = processedCalls.filter((call: any) => {
        let queueExtension = "";
        if (call.queueName) {
          queueExtension = call.queueName.split("@")[0];
        } else if (call.queue) {
          queueExtension = call.queue.split("@")[0];
        }
        let queueName = queueExtension;
        const queueObj = appsQueues.find((q: any) => q.extension === queueExtension);
        if (queueObj && queueObj.name) {
          queueName = queueObj.name;
        }
        return filters.selectedQueue!.includes(queueName);
      });
    }

    // Filter by Number
    if (filters.selectedNumber && Array.isArray(filters.selectedNumber) && filters.selectedNumber.length > 0) {
      processedCalls = processedCalls.filter((call: any) => {
        const callerIDNumber = call.memberCIDNumber || call.callerIDNumber || "";
        const formattedNumber = Utils.formatPhoneNumber(callerIDNumber);
        return filters.selectedNumber!.includes(formattedNumber);
      });
    }

    // Filter by Duration
    if (filters.selectedDuration) {
      processedCalls = processedCalls.filter((call: any) => {
        const now = Math.floor(Date.now() / 1000); // current epoch in seconds
        const callStart = call.calledTime || call.joinedEpoch; // make sure it's in seconds too

        if (!callStart) return false; // no start time

        const durationInMinutes = Math.floor((now - callStart) / 60);
        switch (filters.selectedDuration) {
          case "lt1":
            return durationInMinutes < 1;
          case "1to5":
            return durationInMinutes >= 1 && durationInMinutes <= 5;
          case "5to15":
            return durationInMinutes > 5 && durationInMinutes <= 15;
          case "gt15":
            return durationInMinutes > 15;
          default:
            return true;
        }
      });
    }

    // Sort the filtered results
    if (filters.sortQueue) {
      processedCalls.sort((a: any, b: any) => {
        let queueA = "";
        let queueB = "";

        if (a.queueName) {
          queueA = a.queueName.split("@")[0];
        } else if (a.queue) {
          queueA = a.queue.split("@")[0];
        }

        if (b.queueName) {
          queueB = b.queueName.split("@")[0];
        } else if (b.queue) {
          queueB = b.queue.split("@")[0];
        }

        const queueObjA = appsQueues.find((q: any) => q.extension === queueA);
        const queueObjB = appsQueues.find((q: any) => q.extension === queueB);

        const nameA = queueObjA?.name || queueA;
        const nameB = queueObjB?.name || queueB;

        if (filters.sortQueue === "queue-asc") {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
    }

    if (filters.sortDuration) {
      const now = Date.now();

      processedCalls.sort((a: any, b: any) => {
        const durationA = now - (a.joinedEpoch || a.calledTime || 0);
        const durationB = now - (b.joinedEpoch || b.calledTime || 0);

        if (filters.sortDuration === "duration-asc") {
          return durationA - durationB; // shorter first
        } else {
          return durationB - durationA; // longer first
        }
      });
    }

    if (filters.sortTime) {
      processedCalls.sort((a: any, b: any) => {
        const now = Date.now();
        const timeA = now - (a.joinedEpoch || a.calledTime || 0);
        const timeB = now - (b.joinedEpoch || b.calledTime || 0);

        if (filters.sortTime !== "time-asc") {
          return timeA - timeB; // older calls first
        } else {
          return timeB - timeA; // recent calls first
        }
      });
    }

    return processedCalls;
  }, [queueCalls, filters, agents, appsQueues, agentNameMap, selectedDepartmentIds]);

  return (
    <div className="queued-calls-container">
      <FilterHeader
        heading={`${processActiveCalls?.length} Queue`}
        numbersOptions={filterOptionsData.numbers}
        queuesOptions={filterOptionsData.queues}
        onApplyFilters={handleApplyFilters}
        currentFilters={filters}
      />
      <AppliedFilters 
        filters={filters} 
        handleClearFilters={handleClearFilters} 
        handleRemoveFilter={handleRemoveFilter}
        onFilterUpdate={(key: string, value: any) => {
          dispatchFilters({ type: "SET_FIELD", field: key as any, value });
        }}
        numbersOptions={filterOptionsData.numbers}
        queuesOptions={filterOptionsData.queues}
      />

      {processActiveCalls?.length > 0 ? (
        <ul className="queued-calls-list-container">
          {processActiveCalls?.map((item: any) => {
            let agentsExt: string[] = [];
            const offerings = offeringBySession[item.sessionUUID] || [];
            offerings.forEach((agent: any) => {
              const agentName = agent.agent.split("@")[0];
              const ext = agentNameMap[agentName];
              if (ext && !agentsExt.includes(ext)) {
                agentsExt.push(ext);
              }
            });
            let queueExtension = "";
            if (item.queueName) {
              queueExtension = item.queueName.split("@")[0];
            } else if (item.queue) {
              queueExtension = item.queue.split("@")[0];
            }
            let queueName = queueExtension;
            const queueObj = appsQueues.find((q: any) => q.extension === queueExtension);
            if (queueObj && queueObj.name) {
              queueName = queueObj.name;
            }
            const callerIDName = item.memberCIDName || item.callerIDName || "";
            const callerIDNumber = item.memberCIDNumber || item.callerIDNumber || "";


            return (
              <CallCenterCallItemCard
                key={item.sessionUUID}
                phoneNumber={Utils.formatPhoneNumber(callerIDNumber)}
                userName={callerIDName}
                departmentName={queueName}
                joinedEpoch={item.joinedEpoch}
                agentExtensions={agentsExt}
                onEndCall={() => { }}
                isQueued={true}
                onClick={() => { }}
              />
            );
          })}
        </ul>
      ) : (
        <div className="no-calls-message">
          <Typography variant="text-2">
            No queued calls are present right now.
          </Typography>
        </div>
      )}
    </div>
  );
};

export default QueuedCallsList;
